/**
 * Blackwaxx&Co header behaviour (see design-system/project/components/Header/README.md and MegaMenu/README.md).
 *
 * - Rest / active state (homepage only): the bar rests transparent with a centred logo while the page is scrolled 40px
 *   or less, the pointer is not over the header or a panel, focus is not inside it and no menu is open. Only the
 *   `bw-header--rest` class is toggled; all the motion is CSS.
 * - Desktop mega menu: pointer enter on a nav link with tiles opens its panel (switching in place between links);
 *   the logo, the tools, a link without tiles, leaving the header or Esc close it. Enter / ArrowDown on a link opens the
 *   panel and focuses its first tile.
 * - Mobile sheet: opens from the menu button, locks page scroll, traps focus, one accordion section open at a time.
 * - Keeps Horizon's --header-height and --header-group-height in step so its sticky offsets still work.
 */
const REST_SCROLL_LIMIT = 40;
const DESKTOP_QUERY = '(min-width: 860px)';

class BwHeader extends HTMLElement {
  #bar;
  #links = [];
  #panels = new Map();
  #openKey = null;
  #hovered = false;
  #focused = false;
  #sheetOpen = false;
  #lastPointerType = 'mouse';
  #resizeObserver;
  #scrollFrame = 0;
  #scroller = null;

  connectedCallback() {
    this.#bar = this.querySelector('.bw-header--site');
    this.#links = [...this.querySelectorAll('[data-bw-panel]')];
    this.#links.forEach((link) => this.#panels.set(link.dataset.bwPanel, this.querySelector(`#${link.getAttribute('aria-controls')}`)));
    this.sheet = this.querySelector('.bw-mnav');
    this.menuButton = this.querySelector('[data-bw-open-menu]');
    this.restEnabled = this.hasAttribute('data-rest');

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerdown', this.#onPointerDown, true);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onClick);
    for (const link of this.#links) link.addEventListener('pointerenter', this.#onLinkEnter);
    for (const el of this.querySelectorAll('.bw-header__tools, .bw-header__logo')) {
      el.addEventListener('pointerenter', this.#onCloseTarget);
    }
    for (const link of this.querySelectorAll('.bw-nav a:not([data-bw-panel])')) {
      link.addEventListener('pointerenter', this.#onCloseTarget);
    }
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    // Horizon scrolls inside .page-wrapper from 990px up and the window below that, so listen to both
    this.#scroller = document.querySelector('.page-wrapper');
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });
    window.matchMedia(DESKTOP_QUERY).addEventListener('change', this.#onBreakpoint);

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this);
    document.querySelector('#header-group') && this.#resizeObserver.observe(document.querySelector('#header-group'));

    this.#syncRest();
    this.#measure();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('scroll', this.#onScroll);
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    window.matchMedia(DESKTOP_QUERY).removeEventListener('change', this.#onBreakpoint);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    if (this.#sheetOpen) document.documentElement.classList.remove('bw-scroll-lock');
  }

  /* ---------- state ---------- */

  get #scrollTop() {
    return Math.max(window.scrollY, this.#scroller ? this.#scroller.scrollTop : 0);
  }

  #syncRest() {
    if (!this.restEnabled || !this.#bar) return;
    const rest =
      this.#scrollTop <= REST_SCROLL_LIMIT && !this.#hovered && !this.#focused && this.#openKey === null && !this.#sheetOpen;
    this.#bar.classList.toggle('bw-header--rest', rest);
  }

  #measure = () => {
    const height = Math.round(this.offsetHeight);
    document.body.style.setProperty('--header-height', `${height}px`);
    const group = document.querySelector('#header-group');
    if (!group) return;
    let groupHeight = 0;
    for (const child of group.children) if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
    document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
  };

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#syncRest();
    });
  };

  /* ---------- pointer and focus ---------- */

  #onPointerDown = (event) => {
    this.#lastPointerType = event.pointerType || 'mouse';
  };

  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#syncRest();
  };

  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = false;
    this.#closePanel();
    this.#syncRest();
  };

  #onLinkEnter = (event) => {
    if (event.pointerType !== 'mouse' || !this.#desktop) return;
    this.#openPanel(event.currentTarget.dataset.bwPanel);
  };

  #onCloseTarget = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#closePanel();
  };

  #onDocumentPointerDown = (event) => {
    if (this.#openKey !== null && !this.contains(event.target)) this.#closePanel();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#syncRest();
  };

  #onFocusOut = (event) => {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.#focused = false;
    if (this.#openKey !== null && !this.#hovered) this.#closePanel();
    this.#syncRest();
  };

  /* ---------- desktop mega menu ---------- */

  get #desktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  #openPanel(key) {
    if (this.#openKey === key) return;
    this.#openKey = key;
    for (const link of this.#links) {
      const isOpen = link.dataset.bwPanel === key;
      link.setAttribute('aria-expanded', String(isOpen));
      this.#panels.get(link.dataset.bwPanel)?.classList.toggle('is-open', isOpen);
    }
    this.#syncRest();
  }

  #closePanel() {
    if (this.#openKey === null) return;
    this.#openKey = null;
    for (const link of this.#links) {
      link.setAttribute('aria-expanded', 'false');
      this.#panels.get(link.dataset.bwPanel)?.classList.remove('is-open');
    }
    this.#syncRest();
  }

  #onKeyDown = (event) => {
    const link = event.target.closest?.('[data-bw-panel]');

    if (event.key === 'Escape') {
      if (this.#sheetOpen) {
        event.preventDefault();
        this.#closeSheet();
      } else if (this.#openKey !== null) {
        const key = this.#openKey;
        const openLink = this.#links.find((l) => l.dataset.bwPanel === key);
        const focusWasInside = this.#panels.get(key)?.contains(event.target) || event.target === openLink;
        this.#closePanel();
        if (focusWasInside) openLink?.focus();
      }
      return;
    }

    if (link && this.#desktop && (event.key === 'Enter' || event.key === 'ArrowDown')) {
      event.preventDefault();
      this.#openPanel(link.dataset.bwPanel);
      this.#panels.get(link.dataset.bwPanel)?.querySelector('.bw-mega__tile')?.focus();
      return;
    }

    if (event.key === 'Tab' && this.#sheetOpen) this.#trapFocus(event);
  };

  #onClick = (event) => {
    const link = event.target.closest?.('[data-bw-panel]');
    // Touch on a tablet: the first tap opens the panel, a second tap follows the link.
    if (link && this.#desktop && this.#lastPointerType === 'touch' && this.#openKey !== link.dataset.bwPanel) {
      event.preventDefault();
      this.#openPanel(link.dataset.bwPanel);
      return;
    }

    if (event.target.closest('[data-bw-open-menu]')) return this.#openSheet();
    if (event.target.closest('[data-bw-close-menu]')) return this.#closeSheet();

    const accordion = event.target.closest('[data-bw-accordion]');
    if (accordion) this.#toggleAccordion(accordion);
  };

  /* ---------- mobile sheet ---------- */

  #openSheet() {
    if (!this.sheet || this.#sheetOpen) return;
    this.#sheetOpen = true;
    this.sheet.inert = false;
    this.sheet.classList.add('is-open');
    this.menuButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('bw-scroll-lock');
    this.#syncRest();
    requestAnimationFrame(() => this.sheet.querySelector('[data-bw-close-menu]')?.focus());
  }

  #closeSheet() {
    if (!this.sheet || !this.#sheetOpen) return;
    this.#sheetOpen = false;
    this.sheet.classList.remove('is-open');
    this.sheet.inert = true;
    this.menuButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('bw-scroll-lock');
    this.menuButton?.focus();
    this.#syncRest();
  }

  #toggleAccordion(button) {
    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    for (const other of this.sheet.querySelectorAll('[data-bw-accordion]')) {
      const open = other === button && willOpen;
      other.setAttribute('aria-expanded', String(open));
      const panel = this.sheet.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !open;
    }
  }

  #trapFocus(event) {
    const focusable = [...this.sheet.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #onBreakpoint = () => {
    if (this.#desktop) this.#closeSheet();
    else this.#closePanel();
  };
}

if (!customElements.get('bw-header')) customElements.define('bw-header', BwHeader);
